import axios from "axios";
import { AgentUploadError, AgentUploadErrorReason } from "../../index";
import { Uploader } from "../../handlers";
/**
 * Handles file uploads for AI agent sessions.
 *
 * Orchestrates a two-step flow:
 * 1. Requests a signed GCS upload URL from the mav-plat backend
 * 2. PUTs the file directly to GCS using the signed URL
 *
 * Mounted on {@link AgentSessions} as `sdk.ai.agents[agent].uploads`.
 */
export class AgentUploads extends Uploader {
    constructor(sdk, agent) {
        super(sdk);
        this.agent = agent;
    }
    async saveUpload({ sessionId, file }) {
        const signedResponse = await this.requestSignedUrl(sessionId, file.name);
        try {
            await this.uploadToGCS(signedResponse.signedURL, file);
        }
        catch (err) {
            // If upload fails with 403, the signed URL may have expired — retry once with a fresh URL.
            if (err instanceof AgentUploadError && err.reason === AgentUploadErrorReason.EXPIRED) {
                const freshResponse = await this.requestSignedUrl(sessionId, file.name);
                await this.uploadToGCS(freshResponse.signedURL, file);
                return {
                    fileUri: cleanFileUri(freshResponse.signedURL),
                    fileName: file.name,
                    expiresAt: freshResponse.expiresAt,
                    sessionId: freshResponse.sessionID
                };
            }
            throw err;
        }
        return {
            fileUri: cleanFileUri(signedResponse.signedURL),
            fileName: file.name,
            expiresAt: signedResponse.expiresAt,
            sessionId: signedResponse.sessionID
        };
    }
    /**
     * Requests a signed upload URL from the mav-plat backend.
     */
    async requestSignedUrl(sessionId, fileName) {
        // Replace special characters in the file name with underscores, keeping only
        // alphanumerics, hyphens, underscores, and the last dot (for the extension).
        const sanitizedFileName = sanitizeFileName(fileName);
        const url = `${this.sdk.urls.baseAiAgentsUrlLegacy}/${this.agent}/signed-url/${sessionId}/${encodeURIComponent(sanitizedFileName)}`;
        try {
            const { data } = await axios.post(url, undefined, 
            // Signed-url uploads haven't migrated to ADK; force legacy auth regardless of the
            // agent's ADK status, since baseAiAgentsUrlLegacy only accepts the legacy custom token.
            await this.sdk.ai.axiosRequestConfig(this.agent, { legacy: true }));
            return data;
        }
        catch (err) {
            throw new AgentUploadError(AgentUploadErrorReason.SIGNED_URL_FAILED, `Failed to get signed URL for file "${fileName}": ${err instanceof Error ? err.message : String(err)}`);
        }
    }
    /**
     * Uploads a file directly to GCS using a signed URL.
     * Uses `fetch` (not axios) because the PUT goes directly to GCS, not through our API.
     */
    async uploadToGCS(signedURL, file) {
        let response;
        try {
            response = await fetch(signedURL, {
                method: "PUT",
                headers: { "Content-Type": "application/octet-stream" },
                body: file
            });
        }
        catch (err) {
            throw new AgentUploadError(AgentUploadErrorReason.UPLOAD_FAILED, `Failed to upload file: ${err instanceof Error ? err.message : String(err)}`);
        }
        if (!response.ok) {
            if (response.status === 403) {
                throw new AgentUploadError(AgentUploadErrorReason.EXPIRED, "Signed URL expired or access denied.");
            }
            throw new AgentUploadError(AgentUploadErrorReason.UPLOAD_FAILED, `GCS upload failed with status ${response.status}: ${response.statusText}`);
        }
    }
}
/**
 * Strips special characters from a filename, keeping only alphanumerics,
 * hyphens, underscores, and the file extension dot.
 *
 * Examples:
 *   "Evergreen Kia IO.xlsx"       → "Evergreen_Kia_IO.xlsx"
 *   "Q2 Report (Final) [v2].csv"  → "Q2_Report_Final_v2.csv"
 *   "my—file's   name!.pdf"       → "my_file_s_name.pdf"
 */
function sanitizeFileName(fileName) {
    const ext = fileName.lastIndexOf(".") > 0 ? fileName.slice(fileName.lastIndexOf(".")) : "";
    const base = fileName.slice(0, fileName.length - ext.length);
    return base.replace(/[^a-zA-Z0-9_-]/g, "_") + ext;
}
/**
 * Converts a signed GCS URL to a `gs://` URI that Vertex AI can access natively.
 *
 * Signed URLs have the form:
 *   `https://storage.googleapis.com/{bucket}/{object}?X-Goog-...`
 *
 * Vertex AI's `genai.NewPartFromURI` expects:
 *   `gs://{bucket}/{object}`
 *
 * The query params (V4 signing credentials) are stripped since the backend
 * accesses GCS via service account, not via signed URL.
 */
function cleanFileUri(signedURL) {
    const url = new URL(signedURL);
    // pathname is "/{bucket}/{object}" — drop leading slash
    return `gs:/${url.pathname}`;
}
//# sourceMappingURL=sessions.uploads.js.map